export type LandingLocale = 'en' | 'ar';
export type LandingThemeMode = 'dark' | 'light';

export const landingPalette = {
  dark: {
    pageBg: '#0F1419',
    text: '#FFFFFF',
    muted: 'rgba(255,255,255,0.65)',
    mutedSoft: 'rgba(255,255,255,0.55)',
    border: 'rgba(255,255,255,0.08)',
    navBg: 'rgba(15, 20, 25, 0.72)',
    cardBg: 'rgba(255,255,255,0.03)',
    sectionAlt: '#111821',
    footerBg: '#080C12',
    accent: '#7DD3FC',
    primaryBtn: '#2563EB',
    primaryBtnHover: '#1D4ED8',
  },
  light: {
    pageBg: '#F4F7FB',
    text: '#0F172A',
    muted: 'rgba(15,23,42,0.68)',
    mutedSoft: 'rgba(15,23,42,0.55)',
    border: 'rgba(15,23,42,0.1)',
    navBg: 'rgba(255,255,255,0.86)',
    cardBg: 'rgba(255,255,255,0.92)',
    sectionAlt: '#E8EEF6',
    footerBg: '#0F172A',
    accent: '#0369A1',
    primaryBtn: '#2563EB',
    primaryBtnHover: '#1D4ED8',
  },
} as const;

export type LandingPalette = (typeof landingPalette)[LandingThemeMode];

type GalleryItem = { id: number; title: string; description: string; imageUrl: string };

const galleryEn: GalleryItem[] = [
  { id: 1, title: 'Categories', description: 'Event and unit categories for organized, effective management.', imageUrl: '/image/Pages/Catg.png' },
  { id: 2, title: 'Dashboard View', description: 'Live dashboard showing key metrics and statistics at a glance.', imageUrl: '/image/Pages/dashboard%202.png' },
  { id: 3, title: 'Dashboard', description: 'Overview of operations and activities across the system.', imageUrl: '/image/Pages/dashboard.png' },
  { id: 4, title: 'Event Reporting', description: 'Register and track events from report to closure.', imageUrl: '/image/Pages/incidents.png' },
  { id: 5, title: 'LHD Form (MIDRMA)', description: 'Large height deviation form per MIDRMA F4.', imageUrl: '/image/Pages/lhd1.png' },
  { id: 6, title: 'LHD Workflow', description: 'Stages for completing and reviewing LHD forms.', imageUrl: '/image/Pages/lhd2.png' },
  { id: 7, title: 'LHD Details', description: 'Deviation form details and recorded data.', imageUrl: '/image/Pages/lhd3.png' },
  { id: 8, title: 'LHD Management', description: 'Manage LHD forms and approvals.', imageUrl: '/image/Pages/lhd4.png' },
  { id: 9, title: 'Shift Handover', description: 'Handover between controllers with open events and notes.', imageUrl: '/image/Pages/shiftHandover.png' },
  { id: 10, title: 'Users', description: 'User, role, and unit management.', imageUrl: '/image/Pages/users.png' },
];

const galleryAr: GalleryItem[] = [
  { id: 1, title: 'التصنيفات', description: 'تصنيفات الأحداث والوحدات لإدارة منظمة وفعّالة.', imageUrl: '/image/Pages/Catg.png' },
  { id: 2, title: 'عرض لوحة التحكم', description: 'لوحة حية تُظهر المؤشرات والإحصاءات الرئيسية.', imageUrl: '/image/Pages/dashboard%202.png' },
  { id: 3, title: 'لوحة التحكم', description: 'نظرة عامة على العمليات والأنشطة عبر النظام.', imageUrl: '/image/Pages/dashboard.png' },
  { id: 4, title: 'تسجيل الأحداث', description: 'تسجيل وتتبع الأحداث من الإبلاغ حتى الإغلاق.', imageUrl: '/image/Pages/incidents.png' },
  { id: 5, title: 'نموذج LHD (MIDRMA)', description: 'نموذج انحراف الارتفاع الكبير وفق MIDRMA F4.', imageUrl: '/image/Pages/lhd1.png' },
  { id: 6, title: 'سير عمل LHD', description: 'مراحل إكمال ومراجعة نماذج LHD.', imageUrl: '/image/Pages/lhd2.png' },
  { id: 7, title: 'تفاصيل LHD', description: 'تفاصيل نموذج الانحراف والبيانات المسجّلة.', imageUrl: '/image/Pages/lhd3.png' },
  { id: 8, title: 'إدارة LHD', description: 'إدارة نماذج LHD والموافقات.', imageUrl: '/image/Pages/lhd4.png' },
  { id: 9, title: 'تسليم الوردية', description: 'تسليم بين المراقبين مع الأحداث المفتوحة والملاحظات.', imageUrl: '/image/Pages/shiftHandover.png' },
  { id: 10, title: 'المستخدمون', description: 'إدارة المستخدمين والأدوار والوحدات.', imageUrl: '/image/Pages/users.png' },
];

export const landingCopy = {
  en: {
    nav: {
      home: 'Home',
      stats: 'Stats',
      modules: 'Modules',
      about: 'About',
      signIn: 'Sign In',
      langToggle: 'العربية',
    },
    hero: {
      brandSub: 'Aviation Electronic Logbook',
      headline: 'Air Navigation Operational Logging',
      body: 'Unify real-time watch handovers, system events, incident records, and MIDRMA LHD reporting across ATS, AIM, SAR, and CNS teams with live operational visibility and secure Avi Sky integration.',
      signIn: 'Sign In',
      requestDemo: 'Request Demo',
    },
    stats: {
      title: 'Live Ops Snapshot',
      subtitle: 'Automatic updated aggregated operational metrics from Avi eLog.',
      totalIncidents: 'Total Events',
      activeUsers: 'Active Users',
      avgResponse: 'Avg Response',
      sla: 'Closed on time',
    },
    workflow: {
      title: 'End-to-End Operations Workflow',
      subtitle: 'From first entry to shift close-out, every step stays connected in one electronic logbook.',
      steps: [
        { n: '1', title: 'Log', description: 'Controllers capture events and operational activity as they happen.' },
        { n: '2', title: 'Track', description: 'Follow status, priority, and unit ownership through to resolution.' },
        { n: '3', title: 'Handover', description: 'Pass open work and notes cleanly between shifts without losing context.' },
        { n: '4', title: 'Report', description: 'Produce MIDRMA LHD forms and operational reports ready for review.' },
      ],
    },
    modules: {
      title: 'Platform Modules',
      subtitle: 'Everything your electronic logbook needs — from event capture to MIDRMA reporting.',
      posterAlt: 'Aviation Electronic Logbook cover',
      items: [
        { key: 'lhd', title: 'LHD Forms (MIDRMA)', description: 'Large height deviation forms (MIDRMA F4) for documenting and analyzing deviations of 300 ft or more.', metaSuffix: 'total forms' },
        { key: 'handover', title: 'Shift Handover', description: 'Handover between controllers with open events and operational notes.', metaSuffix: 'total handovers' },
        { key: 'dashboard', title: 'Smart Dashboard', description: 'Real-time overview of operational activity across units and shifts.', meta: 'Live overview' },
        { key: 'incidents', title: 'Event Management', description: 'Track events from first report through closure with full audit trail.', meta: 'Report to close' },
        { key: 'users', title: 'Users & Units', description: 'Role-based access with unit assignments for ACC and related operations.', meta: 'Access control' },
        { key: 'reports', title: 'Reports & Configuration', description: 'Operational reports plus system configuration for categories, areas, and more.', meta: 'Insights & setup' },
      ],
    },
    opsPulse: {
      title: 'Ops Pulse',
      subtitle: 'Live local and Zulu clocks with a radar view of sample operational activity.',
      signInCta: 'Sign in to access your dashboard',
    },
    integration: {
      title: 'Seamless AVISky Integration',
      body: 'Aviation Electronic Logbook syncs events to Avi Sky via secure webhooks and HMAC signatures, so safety workflows stay aligned without duplicate entry.',
      bullets: [
        'Outbound outbox with retry and dead-letter monitoring',
        'HMAC-SHA256 payload verification',
        'Incoming status updates from Avi Sky',
      ],
      status: 'Connected & Synced',
      hint: 'Monitor delivery from the AVISky Sync console after sign-in.',
    },
    about: {
      title: 'Avi eLog - System Screenshots',
    },
    footer: {
      tagline: 'Aviation Electronic Logbook — operational logging for air navigation units.',
      contact: 'Contact',
      companion: 'Companion SMS platform',
      rights: 'All rights reserved.',
    },
    login: {
      title: 'Welcome Back',
      subtitle: 'Sign in to access your dashboard.',
      username: 'Username',
      password: 'Password',
      submit: 'Sign In',
    },
    gallery: galleryEn,
  },
  ar: {
    nav: {
      home: 'الرئيسية',
      stats: 'الإحصاءات',
      modules: 'الوحدات',
      about: 'حول',
      signIn: 'تسجيل الدخول',
      langToggle: 'English',
    },
    hero: {
      brandSub: 'سجل الملاحة الجوي الإلكتروني',
      headline: 'تسجيل تشغيلي للملاحة الجوية',
      body: 'توحيد تسليم المراقبة والأحداث التشغيلية وسجلات الأحداث وتقارير MIDRMA LHD عبر فرق ATS وAIM وSAR وCNS، مع رؤية تشغيلية حية وتكامل آمن مع Avi Sky.',
      signIn: 'تسجيل الدخول',
      requestDemo: 'طلب عرض تجريبي',
    },
    stats: {
      title: 'لمحة العمليات الحية',
      subtitle: 'مؤشرات تشغيلية مجمّعة من Avi eLog تُحدَّث تلقائياً.',
      totalIncidents: 'إجمالي الأحداث',
      activeUsers: 'المستخدمون النشطون',
      avgResponse: 'متوسط الاستجابة',
      sla: 'أُغلقت في الوقت',
    },
    workflow: {
      title: 'سير العمل التشغيلي من البداية للنهاية',
      subtitle: 'من أول إدخال حتى إغلاق الوردية، كل خطوة متصلة في سجل إلكتروني واحد.',
      steps: [
        { n: '1', title: 'تسجيل', description: 'يسجّل المراقبون الأحداث التشغيلية فور وقوعها.' },
        { n: '2', title: 'تتبع', description: 'تابع الحالة والأولوية وملكية الوحدة حتى الحل.' },
        { n: '3', title: 'تسليم', description: 'مرّر الأعمال المفتوحة والملاحظات بين الورديات دون فقدان السياق.' },
        { n: '4', title: 'تقارير', description: 'أنتج نماذج MIDRMA LHD والتقارير التشغيلية الجاهزة للمراجعة.' },
      ],
    },
    modules: {
      title: 'وحدات المنصة',
      subtitle: 'كل ما يحتاجه سجلك الإلكتروني — من تسجيل الأحداث إلى تقارير MIDRMA.',
      posterAlt: 'غلاف سجل الملاحة الجوي الإلكتروني',
      items: [
        { key: 'lhd', title: 'نماذج LHD (MIDRMA)', description: 'نماذج انحراف الارتفاع الكبير (MIDRMA F4) لتوثيق وتحليل الانحرافات بـ 300 قدم فأكثر.', metaSuffix: 'نموذج إجمالي' },
        { key: 'handover', title: 'تسليم الوردية', description: 'تسليم بين المراقبين مع الأحداث المفتوحة والملاحظات التشغيلية.', metaSuffix: 'تسليم إجمالي' },
        { key: 'dashboard', title: 'لوحة ذكية', description: 'نظرة فورية على النشاط التشغيلي عبر الوحدات والورديات.', meta: 'نظرة حية' },
        { key: 'incidents', title: 'إدارة الأحداث', description: 'تتبع الأحداث من أول إبلاغ حتى الإغلاق مع سجل تدقيق كامل.', meta: 'من الإبلاغ للإغلاق' },
        { key: 'users', title: 'المستخدمون والوحدات', description: 'صلاحيات حسب الدور مع تعيين الوحدات لعمليات ACC وما يرتبط بها.', meta: 'التحكم بالوصول' },
        { key: 'reports', title: 'التقارير والإعداد', description: 'تقارير تشغيلية وإعداد النظام للتصنيفات والمناطق والمزيد.', meta: 'رؤى وإعداد' },
      ],
    },
    opsPulse: {
      title: 'نبض العمليات',
      subtitle: 'ساعات محلية وزولو حية مع عرض رادار لنشاط تشغيلي نموذجي.',
      signInCta: 'سجّل الدخول للوصول إلى لوحة التحكم',
    },
    integration: {
      title: 'تكامل سلس مع AVISky',
      body: 'يزامن سجل الملاحة الجوي الإلكتروني الأحداث إلى Avi Sky عبر webhooks وتوقيعات HMAC، لتبقى مسارات السلامة متوافقة دون إدخال مزدوج.',
      bullets: [
        'صندوق إرسال مع إعادة المحاولة ومراقبة الرسائل الميتة',
        'التحقق من الحمولة بـ HMAC-SHA256',
        'تحديثات حالة الأحداث الواردة من Avi Sky',
      ],
      status: 'متصل ومتزامن',
      hint: 'راقب التسليم من وحدة AVISky Sync بعد تسجيل الدخول.',
    },
    about: {
      title: 'لقطات نظام Avi eLog',
    },
    footer: {
      tagline: 'سجل الملاحة الجوي الإلكتروني — تسجيل تشغيلي لوحدات الملاحة الجوية.',
      contact: 'تواصل',
      companion: 'منصة SMS المرافقة',
      rights: 'جميع الحقوق محفوظة.',
    },
    login: {
      title: 'مرحباً بعودتك',
      subtitle: 'سجّل الدخول للوصول إلى لوحة التحكم.',
      username: 'اسم المستخدم',
      password: 'كلمة المرور',
      submit: 'تسجيل الدخول',
    },
    gallery: galleryAr,
  },
} as const;

export type LandingCopy = (typeof landingCopy)['en'];

export function getLandingCopy(locale: LandingLocale): LandingCopy {
  return landingCopy[locale] as LandingCopy;
}
